import React, { useEffect, useRef, useState } from "react";
import classes from "./ThreeWayMatching.module.css";
import axios from "axios";
import { API_ENDPOINT } from "../../../services/api/resources";
import { useSelector } from "react-redux";
import { Button } from "@mui/material";
import HeaderTitle from "../../../components/common/HeaderTitle/HeaderTitle";
import InvoiceChart from "./Charts/InvoiceCharts";
import VendorChart from "./Charts/VendorOnboardChart";
import VendorStatusPie from "./Charts/StatusPie";
import VendorOnboardChart from "./Charts/VendorOnboardDateChart";
import VendorInvoiceDashboard from "./Charts/VendorInvoiceDash";
import VendorInvoiceTable from "./Charts/VendorInvoiceTable";
import InvoiceStatusTable from "./Charts/InvoiceStatusTable";
import PendingESignatureTable from "./Charts/EsignStatus";
import MerchantInvoiceDelay from "../DashboardTab/MerchantInvoiceDelay";
import CountCards from "./Charts/CountCards";
import { useLocation } from "react-router-dom";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { saveAs } from "file-saver";
import InvoiceSampleItemsList from "../../../assets/files/InvoiceSampleItemsList.xlsx";

const InvoiceAutomation = () => {
  const location = useLocation();
  const loginData = useSelector((state) => state.loginData.value);

  const [selectedFile, setSelectedFile] = useState(null);
  const [invoiceList, setInvoiceList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [invoicepage, setInvoicePage] = useState(false);
  const [userStatus, setUserStatus] = useState({});
  const [exporting, setExporting] = useState(false);

  const invoiceUploadInputRef = useRef(null);
  const dashboardOverviewRef = useRef(null);
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
    setInvoiceList([]);
    setUploadError("");
  };

  const handleUploadDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    setInvoiceList([]);
    setUploadError("");
  };

  const handleUploadDragOver = (e) => e.preventDefault();

  const handleUpload = async () => {
    if (!selectedFile) return;

    setLoading(true);
    setUploadError("");

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const response = await axios.post(API_ENDPOINT.invoice_generate, formData);

      if (response?.data?.error) {
        setInvoiceList([]);
        setUserStatus({});
        setUploadError(String(response.data.error));
        return;
      }

      const invoices = response.data.invoices || [];
      let progressMap = {};

      for (let i = 0; i < invoices.length; i++) {
        const invoice = invoices[i];

        progressMap[invoice.vendor.email] = `Generating... ${Math.round(
          ((i + 1) / invoices.length) * 100
        )}%`;

        setUserStatus({ ...progressMap });

        await new Promise((resolve) => setTimeout(resolve, 300));

        progressMap[invoice.vendor.email] = "✅ Done!";
        setUserStatus({ ...progressMap });
      }

      setInvoiceList(invoices);
    } catch (error) {
      setInvoiceList([]);
      setUserStatus({});
      setUploadError(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.response?.data?.response ||
          "Upload failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = (invoice) => {
    const byteChars = atob(invoice.pdf_base64);
    const byteArray = new Uint8Array(byteChars.length);

    for (let i = 0; i < byteChars.length; i++) {
      byteArray[i] = byteChars.charCodeAt(i);
    }

    const blob = new Blob([byteArray], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = invoice.filename || "Invoice.pdf";
    a.click();

    URL.revokeObjectURL(url);
  };

  const switchInvoice = () => {
    setInvoicePage((prev) => !prev);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const action = params.get("action");

    if (action === "create") {
      setInvoicePage(true);
    }

    if (action === "download") {
      downloadScreenAsPDF();
    }
  }, [location.search]);

  const downloadScreenAsPDF = async () => {
    try {
      setExporting(true);
      const dashboardOverview = dashboardOverviewRef.current;

      if (!dashboardOverview) {
        throw new Error("Dashboard overview is not available for export.");
      }

      await new Promise((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(resolve)),
      );
      try {
        if (document.fonts?.ready) await document.fonts.ready;
      } catch (_) {}

      const exportWidth = 1280;
      const canvas = await html2canvas(dashboardOverview, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: "#ffffff",
        logging: false,
        scrollX: 0,
        scrollY: 0,
        width: exportWidth,
        height: Math.ceil(dashboardOverview.scrollHeight),
        windowWidth: exportWidth,
        windowHeight: Math.ceil(dashboardOverview.scrollHeight),
        onclone: (clonedDocument) => {
          const clonedRoot = clonedDocument.querySelector(
            '[data-trade-claims-dashboard-export="true"]',
          );
          if (clonedRoot) {
            clonedRoot.style.width = `${exportWidth}px`;
            clonedRoot.style.maxWidth = "none";
            clonedRoot.style.overflow = "visible";
          }
          const sourceCanvases = dashboardOverview.querySelectorAll("canvas");
          clonedDocument.querySelectorAll("canvas").forEach((canvas, index) => {
            const sourceCanvas = sourceCanvases[index];
            if (!sourceCanvas) return;
            const image = clonedDocument.createElement("img");
            image.src = sourceCanvas.toDataURL("image/png");
            image.style.width = `${sourceCanvas.clientWidth}px`;
            image.style.height = `${sourceCanvas.clientHeight}px`;
            canvas.replaceWith(image);
          });
          clonedDocument.querySelectorAll("*").forEach((node) => {
            node.style.boxShadow = "none";
            node.style.textShadow = "none";
            node.style.backgroundImage = "none";
          });
          clonedDocument.documentElement.style.width = `${exportWidth}px`;
          clonedDocument.body.style.width = `${exportWidth}px`;
        },
      });

      const pdf = new jsPDF("p", "mm", "a4");
      const margin = 8;
      const usableWidth = pdf.internal.pageSize.getWidth() - margin * 2;
      const usableHeight = pdf.internal.pageSize.getHeight() - margin * 2;
      const pageHeightPx = Math.floor((usableHeight * canvas.width) / usableWidth);
      const sliceCanvas = document.createElement("canvas");
      const sliceContext = sliceCanvas.getContext("2d");
      let sourceY = 0;
      let renderedPage = false;

      while (sliceContext && sourceY < canvas.height) {
        const sliceHeight = Math.min(pageHeightPx, canvas.height - sourceY);
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = sliceHeight;
        sliceContext.fillStyle = "#ffffff";
        sliceContext.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
        sliceContext.drawImage(canvas, 0, sourceY, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight);
        if (renderedPage) pdf.addPage();
        pdf.addImage(sliceCanvas.toDataURL("image/jpeg", 0.95), "JPEG", margin, margin, usableWidth, (sliceHeight * usableWidth) / canvas.width);
        renderedPage = true;
        sourceY += sliceHeight;
      }

      if (!renderedPage) throw new Error("Dashboard image could not be created.");
      pdf.save("Trade-Claims-Dashboard-Report.pdf");
    } catch (error) {
      console.error("Dashboard PDF export failed:", error);
      alert(error?.message || "Could not export the dashboard report.");
    } finally {
      setExporting(false);
    }

    return;

    try {
      setExporting(true);
      const dashboardOverview = dashboardOverviewRef.current;

      if (!dashboardOverview) {
        throw new Error("Dashboard overview is not available for export.");
      }

      const charts = Array.from(dashboardOverview.querySelectorAll("canvas"));
      if (!charts.length) {
        throw new Error("Dashboard charts are still loading. Please try again in a moment.");
      }

      const pdf = new jsPDF("l", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 12;
      const chartWidth = (pageWidth - margin * 3) / 2;
      const chartHeight = 116;
      const drawHeader = () => {
        pdf.setTextColor(15, 23, 42);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(16);
        pdf.text("Trade Claims Automation", margin, 13);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);
        pdf.text(`Exported: ${new Date().toLocaleString("en-IN")}`, margin, 19);
      };
      const addChart = (chart, index, x, y, width = chartWidth, height = chartHeight) => {
        const title = chart.closest("section")?.querySelector("h2")?.textContent || `Dashboard Chart ${index + 1}`;
        const imageCanvas = document.createElement("canvas");
        imageCanvas.width = chart.width;
        imageCanvas.height = chart.height;
        const imageContext = imageCanvas.getContext("2d");

        if (!imageContext) {
          throw new Error("Could not prepare a dashboard chart for export.");
        }

        imageContext.fillStyle = "#ffffff";
        imageContext.fillRect(0, 0, imageCanvas.width, imageCanvas.height);
        imageContext.drawImage(chart, 0, 0);
        pdf.setTextColor(15, 23, 42);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(10);
        pdf.text(title.trim(), x, y);
        pdf.addImage(
          imageCanvas.toDataURL("image/jpeg", 0.95),
          "JPEG",
          x,
          y + 4,
          width,
          height,
        );
      };

      drawHeader();
      const cards = Array.from(dashboardOverview.querySelectorAll('[class*="card"]')).filter(
        (node) => node.tagName === "ARTICLE",
      );
      const cardWidth = (pageWidth - margin * 5) / 4;
      cards.slice(0, 4).forEach((card, index) => {
        const x = margin + index * (cardWidth + margin);
        const lines = (card.innerText || "").split("\n").filter(Boolean);
        pdf.setDrawColor(226, 232, 240);
        pdf.setFillColor(255, 255, 255);
        pdf.roundedRect(x, 27, cardWidth, 29, 3, 3, "FD");
        pdf.setTextColor(51, 65, 85);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(8);
        pdf.text(lines[0] || "-", x + 4, 34);
        pdf.setFontSize(15);
        pdf.text(lines[1] || "0", x + 4, 42);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(6.5);
        pdf.text(pdf.splitTextToSize(lines.slice(2).join(" • "), cardWidth - 8), x + 4, 49);
      });
      addChart(charts[0], 0, margin, 66);
      if (charts[1]) addChart(charts[1], 1, margin * 2 + chartWidth, 66);

      if (charts[2]) {
        pdf.addPage();
        drawHeader();
        addChart(charts[2], 2, margin, 30);
        if (charts[3]) addChart(charts[3], 3, margin * 2 + chartWidth, 30);
      }

      if (charts[4]) {
        pdf.addPage();
        drawHeader();
        addChart(charts[4], 4, margin, 30, pageWidth - margin * 2, 145);
      }

      pdf.save("Trade-Claims-Dashboard-Report.pdf");
    } catch (err) {
      console.error("PDF export failed:", err);
      alert(err?.message || "Could not export the trade claims report. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className={classes.invoicePageRoot}>
      <div className={classes.headerContainer} data-export-section>
        <HeaderTitle titleText={"Trade Claims Automation"} />

        <div className={classes.headerActions} data-export-ignore>
          {!invoicepage && (
            <Button
              variant="contained"
              onClick={downloadScreenAsPDF}
              disabled={exporting}
              sx={{
                fontSize: "13px",
                fontFamily: "gilroyBold",
                textTransform: "none",
                borderRadius: "8px",
                px: 2.4,
                py: 1,
              }}
            >
              {exporting ? "Exporting..." : "Export Report"}
            </Button>
          )}

          {loginData.access_type !== "Buyer" && (
            <Button
              variant="contained"
              onClick={switchInvoice}
              sx={{
                fontFamily: "gilroyBold",
                fontSize: "13px",
                textTransform: "none",
                borderRadius: "8px",
                px: 2.4,
                py: 1,
              }}
            >
              {!invoicepage ? "Upload Supplier Invoice" : "Back"}
            </Button>
          )}
        </div>
      </div>

      {invoicepage ? (
        <div className={classes.uploadPage}>
          <div className={classes.uploadCard}>
            <div className={classes.uploadHeader}>
              <h2>Merchant Invoice Auto-Generation</h2>
              <p>Upload Excel or CSV file to generate supplier invoices.</p>
            </div>

            <div className={classes.fileUploadWrapper}>
              <div
                className={classes.file_upload}
                onDrop={handleUploadDrop}
                onDragOver={handleUploadDragOver}
              >
                <div className={classes.DocText}>
                  <span>
                    {selectedFile
                      ? selectedFile.name
                      : "Drag and drop file here"}
                  </span>
                  <p>Accepted file formats: .csv, .xls, .xlsx</p>
                </div>

                <input
                  ref={invoiceUploadInputRef}
                  type="file"
                  accept=".csv,.xls,.xlsx"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />

                {!selectedFile ? (
                  <button
                    type="button"
                    onClick={() => invoiceUploadInputRef.current?.click?.()}
                  >
                    Browse
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedFile(null);
                      setInvoiceList([]);
                      setUserStatus({});
                      setUploadError("");

                      if (invoiceUploadInputRef.current) {
                        invoiceUploadInputRef.current.value = "";
                      }
                    }}
                    className={classes.clearBtn}
                  >
                    Clear
                  </button>
                )}
              </div>

              <p
                className={classes.linkBtn}
                onClick={() => saveAs(InvoiceSampleItemsList, "Invoice Sample.xlsx")}
              >
                Download Sample Excel
              </p>
            </div>

            {selectedFile && (
              <div className={classes.generateAction}>
                <Button
                  variant="contained"
                  onClick={handleUpload}
                  disabled={loading}
                  sx={{
                    fontFamily: "gilroyBold",
                    fontSize: "13px",
                    textTransform: "none",
                    borderRadius: "8px",
                    px: 2.5,
                    py: 1,
                  }}
                >
                  {loading ? "Generating..." : "Generate Invoices"}
                </Button>
              </div>
            )}

            {uploadError && <div className={classes.errorBox}>{uploadError}</div>}

            {Object.keys(userStatus).length > 0 && (
              <div className={classes.statusBox}>
                <strong>Status</strong>

                <ul>
                  {Object.entries(userStatus).map(([email, status], i) => (
                    <li key={i}>
                      <span>{email}</span>
                      <b>{status}</b>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {invoiceList.length > 0 && (
              <div className={classes.invoiceTableWrapper}>
                <table className={classes.invoiceTable}>
                  <thead>
                    <tr>
                      <th>Merchant Code / Supplier ID</th>
                      <th>Merchant Name / Supplier Name</th>
                      <th>Email</th>
                      <th>Transaction Amount</th>
                      <th>Invoice</th>
                    </tr>
                  </thead>

                  <tbody>
                    {invoiceList.map((invoice, index) => (
                      <tr key={index}>
                        <td>{invoice?.invoice_id}</td>
                        <td>{invoice.vendor?.company_name}</td>
                        <td>{invoice.vendor?.email}</td>
                        <td>₹{invoice.vendor?.amount}</td>
                        <td>
                          <button
                            onClick={() => downloadPDF(invoice)}
                            className={classes.pdfBtn}
                            title="Download PDF"
                          >
                            📄
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className={classes.dashboardCanvas}>
          <div
            ref={dashboardOverviewRef}
            data-trade-claims-dashboard-export="true"
            style={
              exporting
                ? { width: "1280px", minWidth: "1280px", overflow: "visible" }
                : undefined
            }
          >
            <div className={classes.dashboardCardsSection} data-export-section>
              <CountCards />
            </div>

            <div data-export-section className={classes.dashboardGrid}>
              <div className={classes.dashboardGridItem}>
                <InvoiceChart />
              </div>

              <div className={classes.dashboardGridItem}>
                <VendorChart />
              </div>
            </div>

            <div data-export-section className={classes.dashboardGrid}>
              <div className={classes.dashboardGridItem}>
                <VendorStatusPie />
              </div>

              <div className={classes.dashboardGridItem}>
                <VendorOnboardChart />
              </div>
            </div>

            <div className={classes.dashboardFullWidth} data-export-section>
              <div className={classes.dashboardGridItem}>
                <VendorInvoiceDashboard />
              </div>
            </div>
          </div>

          <div data-export-section className={classes.dashboardTableRow}>
            <div className={classes.dashboardGridItem}>
              <VendorInvoiceTable />
            </div>
          </div>

          <div data-export-section className={classes.dashboardTableRow}>
            <div className={classes.dashboardGridItem}>
              <InvoiceStatusTable />
            </div>
          </div>

          <div data-export-section className={classes.dashboardTableRow}>
            <div className={classes.dashboardGridItem}>
              <PendingESignatureTable />
            </div>
          </div>

          <div data-export-section className={classes.dashboardTableRow}>
            <div className={classes.dashboardGridItem}>
              <MerchantInvoiceDelay />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceAutomation;
